import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, BellRinging, EnvelopeSimple, DeviceMobile, Clock, Globe } from "@phosphor-icons/react";
import { PageHeader } from "../components/Shell";
import { api, FUEL_LABELS, FUEL_COLORS } from "../lib/api";
import { Field, Modal } from "./Fuel";
import { useStation } from "../lib/station";

export default function Settings() {
  const { activeId, active, stations, refresh: refreshStations, setActive } = useStation();
  const [tanks, setTanks] = useState([]);
  const [seeding, setSeeding] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [timezones, setTimezones] = useState([]);
  const [schedule, setSchedule] = useState(null);

  const refresh = async () => {
    if (!activeId) return;
    setTanks(await api.listTanks(activeId));
    try {
      setSchedule(await api.schedulePreview(activeId));
    } catch {
      /* ignore */
    }
  };
  useEffect(() => {
    refresh();
    api.listTimezones().then((r) => setTimezones(r.timezones));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  if (!activeId || !active) return null;

  const updateTank = async (id, body) => {
    await api.updateTank(id, body);
    toast.success("Tank updated");
    refresh();
  };

  const handleSeed = async () => {
    if (!window.confirm("This will erase all of YOUR stations' data and reload sample data. Continue?")) return;
    setSeeding(true);
    try {
      await api.seed();
      toast.success("Sample data loaded");
      await refreshStations();
      refresh();
    } finally {
      setSeeding(false);
    }
  };

  const handleDeleteStation = async (id) => {
    if (stations.length <= 1) {
      toast.error("Cannot delete the last station");
      return;
    }
    if (!window.confirm("Delete this station and ALL its data? This cannot be undone.")) return;
    await api.deleteStation(id);
    toast.success("Station deleted");
    await refreshStations();
  };

  const handleTestReminders = async () => {
    setSendingTest(true);
    try {
      const res = await api.runReminders(activeId);
      if (res.items === 0) {
        toast.info("No items currently due — nothing to send");
      } else if (res.email?.sent || res.sms?.sent) {
        toast.success(`Sent ${res.items}-item digest`);
      } else {
        toast.info(
          `Built ${res.items}-item digest. Provider keys missing — message was logged. Add RESEND_API_KEY / TWILIO_* to backend/.env to actually send.`
        );
      }
    } catch {
      toast.error("Failed to run reminders");
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Settings"
        subtitle="Stations · reminders · tanks"
        action={
          <button
            onClick={() => setShowAdd(true)}
            data-testid="add-station-btn"
            className="h-11 px-4 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] font-bold text-sm uppercase tracking-wider flex items-center gap-2"
          >
            <Plus size={16} weight="bold" /> Station
          </button>
        }
      />

      <h2 className="font-display font-bold uppercase tracking-wider text-sm text-gray-400 mb-3">Stations</h2>
      <div className="grid gap-2 mb-6" data-testid="stations-list">
        {stations.map((s) => (
          <div
            key={s.id}
            className={`surface rounded-lg p-3 flex items-center justify-between ${s.id === activeId ? "border-[#FF6B00]/60" : ""}`}
            data-testid={`station-row-${s.id}`}
          >
            <button onClick={() => setActive(s.id)} className="flex-1 text-left" data-testid={`activate-station-${s.id}`}>
              <div className="text-sm font-bold text-white">{s.name}</div>
              {s.address && <div className="text-xs text-gray-500">{s.address}</div>}
              {s.id === activeId && (
                <div className="text-[10px] uppercase tracking-wider text-[#FF6B00] mt-1">Active</div>
              )}
            </button>
            <button
              onClick={() => handleDeleteStation(s.id)}
              data-testid={`delete-station-${s.id}`}
              className="h-9 w-9 grid place-items-center rounded-lg text-gray-500 hover:text-[#EF4444]"
              disabled={stations.length <= 1}
            >
              <Trash size={16} weight="bold" />
            </button>
          </div>
        ))}
      </div>

      <h2 className="font-display font-bold uppercase tracking-wider text-sm text-gray-400 mb-3">
        Reminders · {active.name}
      </h2>
      <NotificationSettings
        station={active}
        timezones={timezones}
        onSaved={async () => {
          await refreshStations();
          refresh();
        }}
      />

      {schedule && (
        <div className="surface rounded-lg p-4 mt-3" data-testid="schedule-preview">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <Clock size={22} weight="bold" className="text-[#FF6B00] mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-bold text-white">Scheduled digest</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {active.reminders_enabled ? "Enabled" : "Paused"} · Next run:{" "}
                  <span className="font-mono text-gray-300">{schedule.next_run_local}</span>
                </div>
                {schedule.last_run && (
                  <div className="text-[10px] text-gray-600 mt-1 font-mono">
                    Last: {new Date(schedule.last_run.ran_at).toLocaleString()} · {schedule.last_run.items} items ·{" "}
                    {schedule.last_run.email_sent ? "✓email" : "·"} {schedule.last_run.sms_sent ? "✓sms" : ""}
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={handleTestReminders}
              disabled={sendingTest}
              data-testid="run-reminders-btn"
              className="h-11 px-3 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] disabled:opacity-60 text-white font-bold uppercase tracking-wider text-xs whitespace-nowrap"
            >
              {sendingTest ? "…" : "Send now"}
            </button>
          </div>
        </div>
      )}

      <h2 className="font-display font-bold uppercase tracking-wider text-sm text-gray-400 mt-7 mb-3">
        Tanks · {active.name}
      </h2>
      <div className="grid gap-3 mb-6">
        {tanks.map((t) => (
          <TankSettings key={t.id} tank={t} onSave={updateTank} />
        ))}
      </div>

      <h2 className="font-display font-bold uppercase tracking-wider text-sm text-gray-400 mb-3">Data</h2>
      <div className="surface rounded-lg p-4">
        <div className="text-sm text-gray-300 font-medium">Reset & reload sample data</div>
        <div className="text-xs text-gray-500 mt-1 mb-3">
          Wipes all of YOUR stations and their data, then loads a fresh single-station demo (owned by your account).
        </div>
        <button
          onClick={handleSeed}
          disabled={seeding}
          data-testid="seed-btn"
          className="h-12 px-4 rounded-lg surface-elevated text-[#FF6B00] font-bold uppercase tracking-wider text-sm hover:bg-white/[0.04] disabled:opacity-60"
        >
          {seeding ? "Loading..." : "Load sample data"}
        </button>
      </div>

      {showAdd && (
        <AddStationForm
          timezones={timezones}
          onClose={() => setShowAdd(false)}
          onCreated={async (id) => {
            setShowAdd(false);
            await refreshStations();
            setActive(id);
          }}
        />
      )}
    </div>
  );
}

function NotificationSettings({ station, timezones, onSaved }) {
  const [email, setEmail] = useState(station.notification_email || "");
  const [phone, setPhone] = useState(station.notification_phone || "");
  const [defaultDays, setDefaultDays] = useState(station.default_reminder_days ?? 7);
  const [reminderHour, setReminderHour] = useState(station.reminder_hour ?? 7);
  const [tz, setTz] = useState(station.reminder_timezone || "America/New_York");
  const [enabled, setEnabled] = useState(station.reminders_enabled !== false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEmail(station.notification_email || "");
    setPhone(station.notification_phone || "");
    setDefaultDays(station.default_reminder_days ?? 7);
    setReminderHour(station.reminder_hour ?? 7);
    setTz(station.reminder_timezone || "America/New_York");
    setEnabled(station.reminders_enabled !== false);
  }, [station]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.updateStation(station.id, {
        notification_email: email,
        notification_phone: phone,
        default_reminder_days: parseInt(defaultDays, 10) || 0,
        reminder_hour: parseInt(reminderHour, 10) || 0,
        reminder_timezone: tz,
        reminders_enabled: enabled,
      });
      toast.success("Reminder settings saved");
      onSaved && onSaved();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="surface rounded-lg p-4 grid gap-3" data-testid="notification-settings">
      <Field label="Reminder Email">
        <div className="relative">
          <EnvelopeSimple size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="owner@example.com"
            className="input-dark pl-9"
            data-testid="settings-email"
          />
        </div>
      </Field>
      <Field label="Reminder Phone (E.164)">
        <div className="relative">
          <DeviceMobile size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+14155551212"
            className="input-dark pl-9"
            data-testid="settings-phone"
          />
        </div>
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Digest hour (local)">
          <div className="relative">
            <Clock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <select
              value={reminderHour}
              onChange={(e) => setReminderHour(e.target.value)}
              className="input-dark pl-9"
              data-testid="settings-hour"
            >
              {Array.from({ length: 24 }).map((_, h) => (
                <option key={h} value={h}>
                  {String(h).padStart(2, "0")}:00
                </option>
              ))}
            </select>
          </div>
        </Field>
        <Field label="Timezone">
          <div className="relative">
            <Globe size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <select
              value={tz}
              onChange={(e) => setTz(e.target.value)}
              className="input-dark pl-9"
              data-testid="settings-timezone"
            >
              {timezones.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
              {!timezones.includes(tz) && <option value={tz}>{tz}</option>}
            </select>
          </div>
        </Field>
      </div>

      <Field label="Default equipment reminder days before (for new equipment)">
        <input
          type="number"
          min="0"
          value={defaultDays}
          onChange={(e) => setDefaultDays(e.target.value)}
          className="input-dark"
          data-testid="settings-default-days"
        />
      </Field>

      <label className="flex items-center justify-between gap-3 pt-1">
        <div>
          <div className="text-sm font-bold text-white">Enable scheduled digest</div>
          <div className="text-[11px] text-gray-500">Sends once daily at the hour above.</div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          onClick={() => setEnabled((v) => !v)}
          data-testid="settings-enabled-toggle"
          className={`relative h-7 w-12 rounded-full transition-colors ${enabled ? "bg-[#FF6B00]" : "bg-white/[0.1]"}`}
        >
          <span
            className={`absolute top-0.5 h-6 w-6 rounded-full bg-white transition-transform ${enabled ? "translate-x-5" : "translate-x-0.5"}`}
          />
        </button>
      </label>

      <button
        type="submit"
        disabled={saving}
        data-testid="save-notifications-btn"
        className="h-12 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] disabled:opacity-60 text-white font-bold uppercase tracking-wider text-sm"
      >
        {saving ? "Saving..." : "Save Reminder Settings"}
      </button>
    </form>
  );
}

function TankSettings({ tank, onSave }) {
  const [capacity, setCapacity] = useState(tank.capacity_gallons);
  const [current, setCurrent] = useState(tank.current_gallons);
  const [threshold, setThreshold] = useState(tank.low_threshold_pct);

  useEffect(() => {
    setCapacity(tank.capacity_gallons);
    setCurrent(tank.current_gallons);
    setThreshold(tank.low_threshold_pct);
  }, [tank]);

  return (
    <div className="surface rounded-lg p-4" data-testid={`tank-settings-${tank.fuel_type}`}>
      <div className="flex items-center gap-2 mb-3">
        <span className="h-2.5 w-2.5 rounded-full" style={{ background: FUEL_COLORS[tank.fuel_type] }} />
        <span className="font-bold text-white text-sm uppercase tracking-wider">{FUEL_LABELS[tank.fuel_type]}</span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <Field label="Capacity (gal)">
          <input
            type="number"
            value={capacity}
            onChange={(e) => setCapacity(parseFloat(e.target.value) || 0)}
            className="input-dark"
            data-testid={`settings-capacity-${tank.fuel_type}`}
          />
        </Field>
        <Field label="Current (gal)">
          <input
            type="number"
            value={current}
            onChange={(e) => setCurrent(parseFloat(e.target.value) || 0)}
            className="input-dark"
            data-testid={`settings-current-${tank.fuel_type}`}
          />
        </Field>
        <Field label="Low %">
          <input
            type="number"
            value={threshold}
            onChange={(e) => setThreshold(parseFloat(e.target.value) || 0)}
            className="input-dark"
            data-testid={`settings-threshold-${tank.fuel_type}`}
          />
        </Field>
      </div>
      <button
        onClick={() =>
          onSave(tank.id, {
            capacity_gallons: capacity,
            current_gallons: current,
            low_threshold_pct: threshold,
          })
        }
        data-testid={`save-tank-${tank.fuel_type}`}
        className="mt-3 h-11 px-4 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] text-white font-bold uppercase tracking-wider text-xs"
      >
        Save
      </button>
    </div>
  );
}

function AddStationForm({ timezones, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [days, setDays] = useState(7);
  const [hour, setHour] = useState(7);
  const [tz, setTz] = useState("America/New_York");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name) return toast.error("Station name required");
    setSubmitting(true);
    try {
      const created = await api.createStation({
        name,
        address,
        notification_email: email,
        notification_phone: phone,
        default_reminder_days: parseInt(days, 10) || 7,
        reminder_hour: parseInt(hour, 10) || 7,
        reminder_timezone: tz,
      });
      toast.success("Station created");
      onCreated(created.id);
    } catch {
      toast.error("Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Add Station" testId="station-modal">
      <form onSubmit={submit} className="grid gap-3" data-testid="station-form">
        <Field label="Name">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Downtown Station" className="input-dark" data-testid="station-name" required />
        </Field>
        <Field label="Address (optional)">
          <input value={address} onChange={(e) => setAddress(e.target.value)} className="input-dark" data-testid="station-address" />
        </Field>
        <Field label="Notification Email">
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="owner@example.com" className="input-dark" data-testid="station-email" />
        </Field>
        <Field label="Notification Phone (E.164)">
          <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+14155551212" className="input-dark" data-testid="station-phone" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Digest hour">
            <select value={hour} onChange={(e) => setHour(e.target.value)} className="input-dark" data-testid="station-hour">
              {Array.from({ length: 24 }).map((_, h) => (
                <option key={h} value={h}>
                  {String(h).padStart(2, "0")}:00
                </option>
              ))}
            </select>
          </Field>
          <Field label="Timezone">
            <select value={tz} onChange={(e) => setTz(e.target.value)} className="input-dark" data-testid="station-tz">
              {timezones.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Default reminder days before">
          <input type="number" min="0" value={days} onChange={(e) => setDays(e.target.value)} className="input-dark" data-testid="station-days" />
        </Field>
        <button type="submit" disabled={submitting} data-testid="station-submit" className="h-14 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] disabled:opacity-60 font-bold uppercase tracking-wider text-white">
          {submitting ? "Creating..." : "Create Station"}
        </button>
      </form>
    </Modal>
  );
}
